/**
 * WhatsApp enquiry button.
 *
 * Rebuilds the wa.me link whenever the shopper switches variant, so the message
 * always names the variant they are actually looking at.
 *
 * Horizon's variant picker dispatches `shopify:product:select` with
 * detail = { optionValueId, variantId, connectedProductUrl }. It does NOT update
 * location.search on this template, so the URL is not a reliable source — we
 * listen for the event and fall back to a `change` on the picker's radios.
 */
class WhatsAppEnquiry extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    if (!this.link) return;

    try {
      this.variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    this.onSelect = this.onSelect.bind(this);
    this.onChange = this.onChange.bind(this);

    document.addEventListener('shopify:product:select', this.onSelect);
    // Belt and braces: if the event name changes again, the radios still tell us.
    this.picker = this.closest('[class*="product-details"], body')?.querySelector('variant-picker');
    this.picker?.addEventListener('change', this.onChange);

    this.update(this.initialVariantId());
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:product:select', this.onSelect);
    this.picker?.removeEventListener('change', this.onChange);
  }

  static toId(value) {
    // Accepts 123, "123", or "gid://shopify/ProductVariant/123".
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits || null;
  }

  initialVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    return WhatsAppEnquiry.toId(fromUrl) || WhatsAppEnquiry.toId(this.dataset.variantId);
  }

  onSelect(event) {
    const id = WhatsAppEnquiry.toId(event?.detail?.variantId);
    if (id) this.update(id);
  }

  onChange() {
    // Derive the variant from the currently checked options.
    const chosen = [...(this.picker?.querySelectorAll('input:checked') || [])].map((i) => i.value);
    if (!chosen.length) return;
    const match = this.variants.find((v) =>
      chosen.every((c) => v.options.some((o) => String(o) === String(c)))
    );
    if (match) this.update(match.id);
  }

  update(variantId) {
    const v = this.variants.find((x) => String(x.id) === String(variantId)) || this.variants[0];
    if (!v) return;

    const url = new URL(this.dataset.productUrl, window.location.origin);
    if (v.id) url.searchParams.set('variant', v.id);

    const message = (this.dataset.template || '')
      .replaceAll('{product}', this.dataset.productTitle || '')
      .replaceAll('{variant}', v.title && v.title !== 'Default Title' ? v.title : '')
      .replaceAll('{sku}', v.sku || '')
      .replaceAll('{price}', v.price || '')
      .replaceAll('{url}', url.toString())
      // a blank variant/sku leaves a dangling label like "SKU:" — drop those lines
      .replace(/^[ \t]*[^\n:]+:[ \t]*$/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    this.link.href = `https://wa.me/${this.dataset.phone}?text=${encodeURIComponent(message)}`;
  }
}

if (!customElements.get('whatsapp-enquiry')) {
  customElements.define('whatsapp-enquiry', WhatsAppEnquiry);
}
